// ── Pattern Palette ────────────────────────────────────────────
// Collapsible side panel for the Plan screen.
// Tab A: Browse & drag existing patterns onto workflow edges.
// Tab B: AI-generate new patterns (auto-saved to store).

import React, { useState, useEffect, useCallback } from 'react';
import usePatternGenerate from './hooks/usePatternGenerate';
import { authFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function PatternPalette({ collapsed, onToggle, onAddPattern, loading }) {
  const { patterns, generating, loadPatterns, generate } = usePatternGenerate();
  const [tab, setTab] = useState('patterns'); // 'patterns' | 'generate'
  const [genInput, setGenInput] = useState('');
  const [genError, setGenError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [patternDetails, setPatternDetails] = useState({}); // id -> full definition

  useEffect(() => { loadPatterns(); }, [loadPatterns]);

  const handleGenerate = useCallback(async () => {
    setGenError(null);
    try {
      const pattern = await generate(genInput);
      if (pattern) {
        setGenInput('');
        setTab('patterns');
      }
    } catch (err) {
      setGenError('Generation failed: ' + err.message);
    }
  }, [genInput, generate]);

  const handleDragStart = useCallback((e, pattern) => {
    e.dataTransfer.setData('application/pattern-splice', JSON.stringify({
      patternId: pattern.id,
      patternName: pattern.name || pattern.id,
    }));
    e.dataTransfer.effectAllowed = 'copy';
  }, []);

  const handleExpand = useCallback(async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!patternDetails[id]) {
      try {
        const res = await authFetch(`${API_URL}/api/patterns/${id}`);
        const data = await res.json();
        const def = data.versions?.[0]?.definition || data;
        setPatternDetails(prev => ({ ...prev, [id]: def }));
      } catch {}
    }
  }, [expandedId, patternDetails]);

  if (collapsed) {
    return (
      <div className="pp pp--collapsed" onClick={onToggle}>
        <span className="pp-collapsed-label">Patterns</span>
      </div>
    );
  }

  return (
    <div className="pp">
      <div className="pp-header">
        <div className="pp-tabs">
          <button
            className={`pp-tab ${tab === 'patterns' ? 'pp-tab--active' : ''}`}
            onClick={() => setTab('patterns')}
          >
            Patterns
          </button>
          <button
            className={`pp-tab ${tab === 'generate' ? 'pp-tab--active' : ''}`}
            onClick={() => setTab('generate')}
          >
            Generate
          </button>
        </div>
        <button className="pp-close" onClick={onToggle}>✕</button>
      </div>

      {tab === 'patterns' && (
        <div className="pp-list">
          {patterns.length === 0 && (
            <div className="pp-empty">No patterns yet. Generate one or seed from Settings.</div>
          )}
          {patterns.map(p => {
            const isExpanded = expandedId === p.id;
            const detail = patternDetails[p.id];
            const stageCount = detail?.stages ? Object.keys(detail.stages).length : null;

            return (
              <div
                key={p.id}
                className={`pp-card ${isExpanded ? 'pp-card--expanded' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, p)}
              >
                <div className="pp-card-row">
                  <button
                    className="pp-card-expand-btn"
                    onClick={(e) => { e.stopPropagation(); handleExpand(p.id); }}
                    title="Show stages"
                  >
                    {isExpanded ? '▾' : '▸'}
                  </button>
                  <div
                    className={`pp-card-info ${loading ? 'pp-card-info--disabled' : ''}`}
                    onClick={() => !loading && onAddPattern?.(p.name || p.id)}
                    title={loading ? 'Updating workflow...' : `Click to add "${p.name || p.id}" to workflow`}
                  >
                    <span className="pp-card-name">{p.name || p.id}</span>
                    <span className="pp-card-desc">{p.description?.slice(0, 60) || ''}{p.description?.length > 60 ? '...' : ''}</span>
                  </div>
                  {stageCount && <span className="pp-card-badge">{stageCount}</span>}
                  <span className="pp-card-drag">⋮⋮</span>
                </div>

                {isExpanded && detail?.stages && (
                  <div className="pp-card-stages">
                    {Object.entries(detail.stages).map(([name, def]) => (
                      <div key={name} className="pp-card-stage">
                        <span className="pp-card-stage-type">{def.type}</span>
                        <span className="pp-card-stage-name">{name.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                    <div className="pp-card-hint">Click to add, or drag onto an edge to splice</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === 'generate' && (
        <div className="pp-generate">
          <p className="pp-generate-desc">
            Describe a thinking approach and AI will generate a reusable pattern.
          </p>
          <textarea
            className="pp-generate-input"
            placeholder="e.g., 'A pattern that generates ideas, then has three domain experts critique them in parallel, then synthesizes the best ones'"
            value={genInput}
            onChange={(e) => setGenInput(e.target.value)}
            rows={4}
          />
          {genError && <div className="pp-generate-error">{genError}</div>}
          <button
            className="pp-generate-btn"
            onClick={handleGenerate}
            disabled={!genInput.trim() || generating}
          >
            {generating ? 'Generating...' : 'Generate Pattern'}
          </button>
        </div>
      )}
    </div>
  );
}
